// =====================================================
// API Service
// =====================================================

import type { Model3DAlternative, ProgressiveDiscountConfig } from '../types/product';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RequestConfig extends RequestInit {
  params?: Record<string, string>;
}

// Persistent cache using sessionStorage to survive navigation
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes
const CACHE_PREFIX = 'api_cache_';

function getCached<T>(key: string): T | null {
  try {
    const stored = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!stored) return null;
    
    const entry = JSON.parse(stored) as CacheEntry<T>;
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setCache<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now() };
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // sessionStorage might be full - clear old entries
    clearOldCache();
  }
}

function clearOldCache(): void {
  const keys = Object.keys(sessionStorage).filter(k => k.startsWith(CACHE_PREFIX));
  // Remove oldest 50% of entries
  const entries = keys.map(k => {
    try {
      const entry = JSON.parse(sessionStorage.getItem(k) || '{}');
      return { key: k, timestamp: entry.timestamp || 0 };
    } catch {
      return { key: k, timestamp: 0 };
    }
  }).sort((a, b) => a.timestamp - b.timestamp);
  
  entries.slice(0, Math.ceil(entries.length / 2)).forEach(e => sessionStorage.removeItem(e.key));
}

export function clearApiCache(pattern?: string): void {
  const keys = Object.keys(sessionStorage).filter(k => k.startsWith(CACHE_PREFIX));
  if (!pattern) {
    keys.forEach(k => sessionStorage.removeItem(k));
    return;
  }
  keys.filter(k => k.includes(pattern)).forEach(k => sessionStorage.removeItem(k));
}

/**
 * Clear cache for a specific API path (exact match on the path portion).
 * Unlike clearApiCache which does substring match, this only clears exact path keys.
 */
export function clearApiCacheExact(path: string): void {
  const key = CACHE_PREFIX + path;
  sessionStorage.removeItem(key);
}

class ApiService {
  private baseUrl: string;
  /** Set by ShopifyProvider when running embedded */
  public getSessionToken: (() => Promise<string | null>) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getHeaders(): Promise<Headers> {
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    // Priority 1: Shopify App Bridge session token (embedded mode)
    if (this.getSessionToken) {
      try {
        const sessionToken = await this.getSessionToken();
        if (sessionToken) {
          headers.set('Authorization', `Bearer ${sessionToken}`);
          return headers;
        }
      } catch {
        // Fall through to legacy token
      }
    }

    // Priority 2: Legacy token (localStorage/sessionStorage) — transition period
    const token = localStorage.getItem('turbine_token') || sessionStorage.getItem('turbine_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  private buildUrl(path: string, params?: Record<string, string>): string {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }

    return url.toString();
  }

  /** GET binario com a mesma autenticacao das outras chamadas do admin. */
  async getArrayBuffer(path: string, params?: Record<string, string>): Promise<ArrayBuffer> {
    const headers = await this.getHeaders();
    headers.delete('Content-Type');
    const response = await fetch(this.buildUrl(path, params), { headers });
    if (!response.ok) throw new Error(`HTTP ${response.status} em ${path}`);
    return response.arrayBuffer();
  }

  async request<T>(path: string, config: RequestConfig = {}): Promise<{ data: T; status: number }> {
    const { params, ...fetchConfig } = config;
    const url = this.buildUrl(path, params);

    const response = await fetch(url, {
      ...fetchConfig,
      headers: await this.getHeaders(),
      credentials: 'include', // Send cookies for standalone OAuth sessions
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new ApiError(error.message || 'Request failed', response.status);
    }

    const data = await response.json();
    return { data, status: response.status };
  }

  // Cached GET request
  async getCached<T>(path: string, params?: Record<string, string>): Promise<{ data: T; status: number }> {
    const cacheKey = `${path}${params ? JSON.stringify(params) : ''}`;
    const cached = getCached<T>(cacheKey);
    if (cached) {
      console.log('[API Cache] HIT:', path);
      return { data: cached, status: 200 };
    }
    console.log('[API Cache] MISS:', path);
    const result = await this.get<T>(path, params);
    setCache(cacheKey, result.data);
    return result;
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<{ data: T; status: number }> {
    return this.request<T>(path, { method: 'GET', params });
  }

  /** Open a streaming GET (SSE/NDJSON) with auth headers. Returns the raw Response. */
  async streamGet(path: string): Promise<Response> {
    const headers = await this.getHeaders();
    return fetch(this.buildUrl(path), { headers, credentials: 'include' });
  }

  async post<T>(path: string, body?: unknown): Promise<{ data: T; status: number }> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(path: string, body?: unknown): Promise<{ data: T; status: number }> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(path: string, body?: unknown): Promise<{ data: T; status: number }> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(path: string): Promise<{ data: T; status: number }> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  async upload<T>(path: string, file: File, fieldName = 'file'): Promise<{ data: T; status: number }> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const headers = new Headers();

    // Priority 1: Shopify App Bridge session token (embedded mode)
    if (this.getSessionToken) {
      try {
        const sessionToken = await this.getSessionToken();
        if (sessionToken) {
          headers.set('Authorization', `Bearer ${sessionToken}`);
        }
      } catch {
        // Fall through to legacy token
      }
    }

    // Priority 2: Legacy token — transition period
    if (!headers.has('Authorization')) {
      const token = localStorage.getItem('turbine_token') || sessionStorage.getItem('turbine_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const response = await fetch(this.buildUrl(path), {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new ApiError(error.message || 'Upload failed', response.status);
    }

    const data = await response.json();
    return { data, status: response.status };
  }
}

// =====================================================
// Error Class
// =====================================================

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// =====================================================
// API Instance
// =====================================================

export const api = new ApiService(API_BASE_URL);

// =====================================================
// Product API
// =====================================================

export const productApi = {
  // Lista de produtos (com cache de 5 min)
  list: (params?: Record<string, unknown>) => 
    api.getCached<{ products: Product[]; total: number }>('/products', params as Record<string, string>),
  
  // Lista de configs 3D (com cache de 5 min)
  listConfigs: () => 
    api.getCached<{ configs: Array<{
      productId: string;
      productHandle?: string;
      settings?: Record<string, unknown>;
      updatedAt: string;
      hasModel?: boolean;
      hasViewer3d?: boolean;
      has3DInVolumes?: boolean;
      volumeConfigs?: Record<string, { volumeHandle?: string; volumeLabel?: string }>;
    }> }>('/products/configs'),

  // Lista de configs 3D sem cache.
  // Usado como fallback quando uma resposta vazia pode ter vindo de falha transitória.
  listConfigsFresh: () =>
    api.get<{ configs: Array<{
      productId: string;
      productHandle?: string;
      settings?: Record<string, unknown>;
      updatedAt: string;
      hasModel?: boolean;
      hasViewer3d?: boolean;
      has3DInVolumes?: boolean;
      volumeConfigs?: Record<string, { volumeHandle?: string; volumeLabel?: string }>;
    }> }>('/products/configs'),
  
  // Produto individual (com cache)
  get: (id: string) => 
    api.getCached<{ product: Product }>(`/products/${id}`),
  
  // Config do produto (com cache)
  getConfig: (id: string) => 
    api.getCached<{ config: ProductConfig }>(`/products/${id}/config`),
  
  // Config do produto (sem cache — force fresh from server)
  getConfigFresh: (id: string) => 
    api.get<{ config: ProductConfig }>(`/products/${id}/config`),
  
  updateConfig: (id: string, config: ProductConfig) => {
    // Invalidate cached GET so a follow-up read returns fresh state (Bug A defense).
    clearApiCacheExact(`/products/${id}/config`);
    return api.put<{ config: ProductConfig }>(`/products/${id}/config`, config);
  },

  updateProgressiveDiscount: (id: string, progressiveDiscount: ProgressiveDiscountConfig | null) => {
    clearApiCacheExact(`/products/${id}/config`);
    clearApiCache('configs');
    return api.put<{ success: boolean; progressiveDiscount: ProgressiveDiscountConfig | null; changedSettingsKeys: string[]; shopifySync?: unknown }>(
      `/products/${id}/progressive-discount`,
      { progressiveDiscount },
    );
  },

  // Invalidate server-side assembled config cache (forces re-fetch from Shopify)
  refreshConfig: (id: string) => 
    api.post<{ message: string }>(`/products/${id}/refresh-config`),
  
  // Invalidate ALL server-side assembled config caches
  refreshAllConfigs: () =>
    api.post<{ message: string }>('/products/refresh-all-configs'),

  sync: (id: string) => 
    api.post<{ product: Product }>(`/products/${id}/sync`),

  /** Export admin config bundle (settings, viewer3d, VariantConfig snapshot) — read-only. */
  exportProductConfig: (id: string) =>
    api.get<{
      exportedAt: string;
      productId: string;
      handle: string | null;
      viewer3d: Record<string, unknown>;
      settings: Record<string, unknown>;
      variantVolumes: Record<string, unknown>;
    }>(`/products/${id}/export-product-config`),

  /**
   * Merge source product config into targets (best-effort; isolates failures per product).
   * Clears API cache for each affected id on success.
   */
  cloneProductConfig: (
    sourceProductId: string,
    body: { targetProductIds: string[]; volumeMap?: Record<string, string> },
  ) => {
    for (const tid of body.targetProductIds) {
      clearApiCacheExact(`/products/${tid}/config`);
      clearApiCache(`/products/${tid}`);
    }
    return api.post<{
      results: Array<{
        productId: string;
        handle?: string;
        success: boolean;
        warnings?: string[];
        skippedPaths?: string[];
        volumeMap?: Record<string, string>;
        error?: string;
      }>;
      sourceProductId: string;
      successCount: number;
      totalCount: number;
    }>(`/products/${sourceProductId}/clone-config`, body);
  },
};

export interface CustomizerHealthCheck {
  id: string;
  productId: string;
  variantId?: string | null;
  handle?: string | null;
  productType?: string | null;
  modelLabel?: string | null;
  status: 'ok' | 'attention' | 'error' | string;
  reason?: string | null;
  details?: {
    modelUrlCount?: number;
    modelUrls?: string[];
    volumeKeys?: string[];
    payloadHash?: string | null;
    hasProductConfig?: boolean;
    hasPublicPayload?: boolean;
    hasVariantConfig?: boolean;
    fontChecks?: Record<string, unknown>;
    [key: string]: unknown;
  };
  checkedAt?: string;
  createdAt?: string;
}

export const monitoringApi = {
  listCustomizerHealth: () =>
    api.get<{ checks: CustomizerHealthCheck[] }>('/monitoring/customizer-health'),

  revalidateCustomizerProduct: (productId: string, handle?: string | null, variantId?: string | null) =>
    api.post<{ check: CustomizerHealthCheck }>('/monitoring/customizer-health/revalidate', { productId, handle, variantId }),

  rebuildCustomizerCache: (productId: string, handle?: string | null) =>
    api.post<{ ok: boolean; productId: string }>('/monitoring/customizer-health/rebuild-cache', { productId, handle }),

  dismissCustomizerAlert: (productId: string, handle?: string | null, variantId?: string | null) =>
    api.post<{ ok: boolean }>('/monitoring/customizer-health/dismiss', { productId, handle, variantId }),
};

// =====================================================
// Settings API
// =====================================================

export const settingsApi = {
  list: () =>
    api.get<Record<string, { value: unknown; description?: string | null }>>('/settings'),

  update: (key: string, value: unknown, description?: string) =>
    api.put<{ success: boolean; key: string; value: unknown }>(`/settings/${key}`, { value, description }),
};

export interface AppDiscountCombinesWith {
  productDiscounts: boolean;
  orderDiscounts: boolean;
  shippingDiscounts: boolean;
}

export interface AppDiscountCombinationStatus {
  key: string;
  label: string;
  id: string;
  found: boolean;
  discountId: string;
  title: string | null;
  status: string | null;
  combinesWith: AppDiscountCombinesWith;
  errorMessage?: string | null;
}

export const discountsApi = {
  getAppCombinations: () =>
    api.get<{ discounts: AppDiscountCombinationStatus[] }>('/discounts/app-combinations'),

  updateAppCombination: (key: string, combinesWith: AppDiscountCombinesWith) =>
    api.put<{
      key: string;
      label: string;
      id: string;
      ok: boolean;
      userErrors: Array<{ field?: string[]; message: string }>;
      discount: {
        discountId: string;
        title: string | null;
        status: string | null;
        combinesWith: AppDiscountCombinesWith;
      } | null;
    }>(`/discounts/app-combinations/${encodeURIComponent(key)}`, { combinesWith }),

  createAppDiscount: (key: string) =>
    api.post<{ key: string; created: boolean; alreadyExists?: boolean; discountId?: string; title?: string; status?: string }>(
      `/discounts/app-combinations/${encodeURIComponent(key)}/create`,
      {},
    ),

  reactivateAppDiscount: (key: string) =>
    api.post<{ key: string; discountId?: string; title?: string; status?: string; reactivated?: boolean }>(
      `/discounts/app-combinations/${encodeURIComponent(key)}/reactivate`,
      {},
    ),

  deactivateAppDiscount: (key: string) =>
    api.post<{ key: string; discountId?: string; status?: string; message?: string }>(
      `/discounts/app-combinations/${encodeURIComponent(key)}/deactivate`,
      {},
    ),

  syncGlobal: () =>
    api.post<{
      success: boolean;
      synced: number;
      cleared: number;
      errors: number;
      failedGids?: string[];
      mapStats?: { totalVariants: number; totalProducts: number; stepProducts?: number; kindMetaWritten?: number };
    }>(
      '/discounts/sync-global',
      {},
    ),
};

// =====================================================
// Catalog API
// =====================================================

export type CatalogSourceKind = 'productType' | 'model' | 'collection' | 'group' | 'custom' | 'product';

export interface CatalogSource {
  kind: CatalogSourceKind;
  value: string | null;
  groupId?: number | null;
}

export type CatalogImageMode = 'auto' | 'upload' | 'product';

export interface CatalogImageRef {
  mode: CatalogImageMode;
  url: string | null;
  productId: string | null;
}

export interface CatalogModel {
  id: string;
  title: string;
  description?: string;
  sourceGroupId?: number | null;
  productIds: string[];
  sortOrder: number;
  settings?: Record<string, unknown>;
  source?: CatalogSource | null;
  image?: CatalogImageRef | null;
}

export interface CatalogCategory {
  id: string;
  title: string;
  description?: string;
  sortOrder: number;
  models: CatalogModel[];
  settings?: Record<string, unknown>;
  source?: CatalogSource | null;
  image?: CatalogImageRef | null;
}

export interface CatalogHowToStep {
  title: string;
  text: string;
}

export interface CatalogSettings {
  enabled: boolean;
  publicSlug: string;
  title: string;
  subtitle: string;
  layout: 'grid' | 'list';
  navigationMode: 'tabs' | 'sidebar' | 'accordion' | 'filters';
  showSearch: boolean;
  showFilters: boolean;
  showUnavailable: boolean;
  showUnconfigured: boolean;
  only3D?: boolean;
  autoOpenFirstProduct: boolean;
  defaultRenderMode: 'button' | 'inline_iframe';
  buttonLabel: string;
  iframeHeight: string;
  theme?: Record<string, unknown>;
  hint?: { title: string; text: string } | null;
  howTo?: CatalogHowToStep[];
  guarantee?: { title: string; text: string } | null;
  footerNote?: string;
  personalizationWarningEnabled?: boolean;
  personalizationWarning?: string;
  /** Optional Shopify sales-channel publication IDs this catalog targets.
   *  Admin metadata only — empty/undefined keeps the default behavior. */
  salesChannelPublicationIds?: string[];
  /** Logo link in the catalog header. Empty = embed origin → paccoby default. */
  siteUrl?: string;
}

export interface Catalog {
  id?: number;
  name: string;
  description?: string | null;
  slug?: string | null;
  sections: CatalogCategory[];
  settings: CatalogSettings;
  isActive: boolean;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CatalogTaxonomyEntry {
  value: string;
  count: number;
  productType?: string;
  sampleProductIds?: string[];
}

export interface CatalogTaxonomyGroup {
  id: number;
  name: string;
  type: 'volume' | 'produto';
  productCount: number;
  inferredType: string | null;
  inferredModel: string | null;
}

export interface CatalogTaxonomy {
  productTypes: CatalogTaxonomyEntry[];
  models: CatalogTaxonomyEntry[];
  modelsByType: Record<string, CatalogTaxonomyEntry[]>;
  collections: Array<{ value: string; count: number }>;
  volumeGroups: CatalogTaxonomyGroup[];
  productGroups: CatalogTaxonomyGroup[];
}

export const catalogApi = {
  list: () => api.get<{ catalogs: Catalog[] }>('/catalogs'),
  get: (id: string | number) => api.get<{ catalog: Catalog }>(`/catalogs/${id}`),
  create: (catalog: Omit<Catalog, 'id'>) => api.post<{ catalog: Catalog }>('/catalogs', catalog),
  update: (id: string | number, catalog: Partial<Catalog>) => api.put<{ catalog: Catalog }>(`/catalogs/${id}`, catalog),
  delete: (id: string | number) => api.delete<{ success: boolean }>(`/catalogs/${id}`),
  duplicate: (id: string | number) => api.post<{ catalog: Catalog }>(`/catalogs/${id}/duplicate`, {}),
  taxonomy: () => api.get<CatalogTaxonomy>('/catalogs/taxonomy'),
  taxonomyModels: (productType?: string) => api.get<{ models: CatalogTaxonomyEntry[]; productType: string | null }>(
    `/catalogs/taxonomy/models${productType ? `?productType=${encodeURIComponent(productType)}` : ''}`,
  ),
  seedDefault: (regenerate = false) => api.post<{ catalog: Catalog; created: boolean; regenerated?: boolean }>('/catalogs/seed-default', { regenerate }),
  stats: (id: number, opts?: { days?: number; from?: string; to?: string }) => {
    const params = new URLSearchParams();
    if (opts?.days) params.set('days', String(opts.days));
    if (opts?.from) params.set('from', opts.from);
    if (opts?.to) params.set('to', opts.to);
    const qs = params.toString();
    return api.get<CatalogStats>(`/catalogs/${id}/stats${qs ? '?' + qs : ''}`);
  },
};

export interface CatalogStats {
  catalog: { id: number; name: string; slug: string };
  period: { days: number | null; from: string | null; to: string | null };
  totals: {
    opens: number;
    uniqueSessions: number;
    categoryClicks: number;
    itemClicks: number;
    customizerOpens: number;
    cartAdds: number;
    ordersTotal: number;
    ordersCompleted: number;
    ordersAddedToCart: number;
    ordersAbandoned: number;
    ordersDraft: number;
    revenueCompleted: number;
  };
  funnel: Array<{ step: string; count: number; sessions?: number }>;
  topCategories: Array<{ name: string; clicks: number; sessions: number }>;
  topItems: Array<{ name: string; category: string; clicks: number; sessions: number }>;
  daily: Array<{ day: string; opens: number; sessions: number }>;
  eventCounts: Record<string, number>;
}

// =====================================================
// Sales Channels API (Shopify Publications registry)
// =====================================================

export interface SalesChannel {
  id: number;
  publicationId: string;
  publicationName: string | null;
  label: string | null;
  key: string | null;
  enabled: boolean;
  allowedOrigins: string[];
  source: string;
  metadata: Record<string, unknown>;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const salesChannelApi = {
  list: () => api.get<{ channels: SalesChannel[] }>('/sales-channels'),
  sync: () => api.post<{ synced: number; channels: SalesChannel[] }>('/sales-channels/sync'),
  update: (id: number, patch: Partial<Pick<SalesChannel, 'label' | 'key' | 'enabled' | 'allowedOrigins' | 'metadata'>>) =>
    api.put<{ channel: SalesChannel }>(`/sales-channels/${id}`, patch),
};

// Per-channel personalization override stored in SalesChannel.metadata:
//   personalizationOverrides[personalizationId][stepId] = { corporateLogo?, hidden? }
export interface ChannelPersonalizationStepOverride {
  corporateLogo?: boolean;
  hidden?: boolean;
}
export type ChannelPersonalizationOverrides = Record<string, Record<string, ChannelPersonalizationStepOverride>>;

// Lightweight personalization shape for the channel config UI.
export interface PersonalizationSummaryStep {
  id: string;
  name?: string;
  type?: string;
  enabledTypes?: string[];
}
export interface PersonalizationSummary {
  id: number;
  name: string;
  steps?: PersonalizationSummaryStep[];
  stepsCount?: number;
}

export const personalizationApi = {
  list: () => api.get<{ personalizations: PersonalizationSummary[] }>('/personalizations'),
};

// =====================================================
// Alternative Models API (per-product alternative 3D models)
// =====================================================

export const alternativeModelsApi = {
  /** Fetch list of alternative models for a specific product (component) */
  list: (productId: string) => 
    api.getCached<{ alternatives: Model3DAlternative[] }>(
      `/products/${productId}/alternative-models`
    ),
  
  /** Delete an alternative model — returns warning if in use by other products */
  delete: (productId: string, alternativeModelId: number) => {
    clearApiCacheExact(`/products/${productId}/alternative-models`);
    return api.delete<{ success: boolean; inUseBy?: string[] }>(
      `/products/${productId}/alternative-models/${alternativeModelId}`
    );
  },
};

// =====================================================
// Colors API
// =====================================================

export interface ShopifyColor {
  id: string;
  handle: string;
  displayName: string;
  label: string;
  hex: string;
  baseColor: { id: string; handle: string; hex: string; label: string } | null;
  shopifyColor: { id: string; handle: string; hex: string; label: string } | null;
  textureUrl: string | null;
  pattern: unknown | null;
  synonyms: string | null;
}

export interface ShopifyNativeColor {
  id: string;
  handle: string;
  displayName: string;
  label: string;
  hex: string | null;
  image: string | null;
}

export const colorsApi = {
  list: () => 
    api.getCached<{ colors: ShopifyColor[] }>('/colors'),
  /** Fetch all Shopify native color-pattern metaobjects */
  listShopifyColors: () =>
    api.getCached<{ colors: ShopifyNativeColor[] }>('/shopify-colors'),
  /** Fetch default variant → text color mapping */
  getDefaultVariantColors: () =>
    api.getCached<{ colors: Record<string, string> }>('/default-variant-colors'),
  /** Fetch all unique textures from PaccoColor metaobjects */
  listTextures: () =>
    api.getCached<{ textures: Array<{ id?: string; name: string; url: string; volumeHandles: string[]; colorHandle: string | null; colorLabel: string | null; source?: 'shopify' | 'user_upload'; uploadedAt?: string | null; duplicateCount?: number }> }>('/textures'),
  /** Upload a custom texture image (local only) */
  uploadTexture: (fileName: string, fileData: string) =>
    api.post<{ url: string; name: string }>('/upload/texture', { fileName, fileData }),
  /** Upload a custom texture to Shopify CDN (for storefront use) */
  uploadTextureToCdn: (fileName: string, fileData: string) =>
    api.post<{ url: string; localUrl: string; name: string }>('/upload/texture-to-cdn', { fileName, fileData }),
  /** Remove a custom upload from the material texture library */
  deleteTextureUpload: (idOrUrl: string) =>
    api.delete<{ ok: boolean; removed: number }>(`/textures/uploads/${encodeURIComponent(idOrUrl)}`),
};

// =====================================================
// Template API
// =====================================================

export const templateApi = {
  list: () => 
    api.get<{ templates: Template[] }>('/templates'),
  
  get: (id: string) => 
    api.get<{ template: Template }>(`/templates/${id}`),
  
  create: (data: Partial<Template>) => 
    api.post<{ template: Template }>('/templates', data),
  
  update: (id: string, data: Partial<Template>) => 
    api.put<{ template: Template }>(`/templates/${id}`, data),
  
  delete: (id: string) => 
    api.delete<{ success: boolean }>(`/templates/${id}`),
};

// =====================================================
// Asset API
// =====================================================

export const assetApi = {
  list: (type?: string) => 
    api.get<{ assets: Asset[] }>('/assets', type ? { type } : undefined),
  
  upload: (file: File, type: string) => 
    api.upload<{ asset: Asset }>(`/assets?type=${type}`, file),
  
  delete: (id: string) => 
    api.delete<{ success: boolean }>(`/assets/${id}`),
};

export interface PersonalizationDisplayIconAsset {
  id: string;
  name: string;
  url: string;
  mimeType: string;
}

export const personalizationDisplayIconApi = {
  list: () =>
    api.get<{ icons: PersonalizationDisplayIconAsset[] }>('/upload/elements'),

  upload: (fileName: string, fileData: string) =>
    api.post<{ url: string }>('/upload/element', { fileName, fileData }),
};

// =====================================================
// Types (inline for service)
// =====================================================

interface Product {
  id: string;
  title: string;
  handle: string;
  vendor: string;
  productType: string;
  variants: ProductVariant[];
  images: string[];
  options?: Array<{ name: string; values: string[] }>;
}

interface ProductVariant {
  id: string;
  title: string;
  price: string;
  sku?: string;
  available: boolean;
}

interface ProductConfig {
  [key: string]: unknown;
}

interface Template {
  id: string;
  name: string;
  type: string;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface Asset {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  createdAt: string;
}
